import { useContext, useEffect, useMemo } from 'react';
import UsageTransferMetricsView from './UsageTransferMetricsView';
import { store } from '../../../stores/store';
import { ReportContext } from '../context/ReportProvider';
import { formValuesToFoundryParams } from '../Forms/hooks/useReport/util';
import UseReport from '../Forms/hooks/useReport/useReport';

interface Metric {
  name: string;
  value: number | string;
}

interface UsageTransferMetricResponse {
  value?: Record<string, unknown>;
  metrics?: Metric[];
}

type MetricValues = {
  moveItems: number;
  savedAmount: number;
  expiringItems: number;
  wastedAmount: number;
};

const numberFromUnknown = (v: unknown): number => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const extractMetricValueObj = (data: any): Record<string, unknown> => {
  if (!data) return {};

  if (data.value && typeof data.value === 'object' && !Array.isArray(data.value)) {
    return data.value as Record<string, unknown>;
  }

  if (Array.isArray(data) && data[0]?.value && typeof data[0].value === 'object') {
    return data[0].value as Record<string, unknown>;
  }

  return {};
};

const normalizeToMetricsArray = (data: any): Metric[] => {
  if (data?.metrics && Array.isArray(data.metrics)) {
    return data.metrics.map((m: Metric) => ({
      name: m.name,
      value: numberFromUnknown(m.value),
    }));
  }

  const valueObj = extractMetricValueObj(data);
  return Object.entries(valueObj).map(([name, value]) => ({
    name,
    value: numberFromUnknown(value),
  }));
};

const getMetricByIncludes = (metrics: Metric[], keyword: string): number => {
  const found = metrics.find((m) => m.name?.toLowerCase().includes(keyword.toLowerCase()));
  return numberFromUnknown(found?.value);
};

const CONSIGNED_MAP: Record<number, string> = {
  0: 'Owned',
  1: 'Consigned',
  2: 'Both',
};

const DEPLOY_ENV =
  (((window as any)?.RUNTIME_CONFIG?.DEPLOY_ENV) || process.env.REACT_APP_DEPLOY_ENV || '')
    .toString()
    .toLowerCase();

const OBJECT_TYPE = DEPLOY_ENV === 'prod' || DEPLOY_ENV === 'production'
  ? 'QsightPrdlegUsageTransferMetricCard'
  : 'UsageTransferMetricCard';

const METRIC_CARD_ENDPOINT = process.env.REACT_APP_APIGEE_METRIC_CARD_ENDPOINT ?? '';

const buildQueryPayload = (
  reportContext: any
): {
  parameters: {
    hospitalIds: string[];
    departmentIds: string[];
    consignedOrOwned: string;
    additionalFilters: Array<any>;
  };
} => {

  let hospitalIds = [store.commonStore.hospitalId]
    .filter((id): id is number => id !== undefined)
    .map(String);

  let departmentIds = [store.commonStore.departmentId]
    .filter((id): id is number => id !== undefined)
    .map(String);

  const rawFilters =
    reportContext?.reportFilter && Object.keys(reportContext.reportFilter).length > 0
      ? reportContext.reportFilter
      : {
        hospitalId: hospitalIds,
        departmentId: departmentIds,
      };

  hospitalIds = rawFilters.hospitalId ? (Array.isArray(rawFilters.hospitalId) ? rawFilters.hospitalId : [rawFilters.hospitalId]) : hospitalIds;
  departmentIds = rawFilters.departmentId ? (Array.isArray(rawFilters.departmentId) ? rawFilters.departmentId : [rawFilters.departmentId]) : departmentIds;

  const params = formValuesToFoundryParams(rawFilters, OBJECT_TYPE) as Array<{
    type: string;
    field: string;
    value: any;
  }>;

  const consignedParam = params.find((p) => p.field === 'consigned')?.value as number | undefined;
  const consignedOrOwned = consignedParam !== undefined ? CONSIGNED_MAP[consignedParam] : '';

  const expiringParam = params.find((p) => p.field === 'expiringBucket')?.value as number | undefined;
  const expiringWithinDays = expiringParam !== undefined ? expiringParam : undefined;

  const additionalFilters = [
    ...(expiringWithinDays !== undefined
      ? [{
        field: 'expiringBucket',
        type: 'eq',
        value: { numericValue: expiringWithinDays }
      }]
      : []),
    { field: 'transferToDepartmentId', type: 'isNotNull' },
  ];

  return {
    parameters: {
      hospitalIds,
      departmentIds,
      consignedOrOwned,
      additionalFilters,
    },
  };
};

const UsageTransferMetricsContainer: React.FC = () => {
  const reportContext = useContext(ReportContext);

  const queryDataMetricCard = useMemo(() => buildQueryPayload(reportContext), [reportContext]);

  const resultMetricCard = UseReport<UsageTransferMetricResponse>(
    OBJECT_TYPE,
    METRIC_CARD_ENDPOINT,
    queryDataMetricCard
  );

  const isLoading = resultMetricCard.loading === true;

  useEffect(() => {
    reportContext?.setMetricCardsLoading?.(isLoading);
  }, [isLoading, reportContext]);

  const metrics: Metric[] = useMemo(
    () => normalizeToMetricsArray(resultMetricCard.data),
    [resultMetricCard.data]
  );

  const values: MetricValues = useMemo(
    () => ({
      moveItems: getMetricByIncludes(metrics, 'whatCanIMove'),
      savedAmount: getMetricByIncludes(metrics, 'howMuchWillBeSaved'),
      expiringItems: getMetricByIncludes(metrics, 'whatExpiresSoon'),
      wastedAmount: getMetricByIncludes(metrics, 'howMuchWillBeWasted'),
    }),
    [metrics]
  );

  return <UsageTransferMetricsView values={values} isLoading={isLoading} />;
};

export default UsageTransferMetricsContainer;